"use client";

import { ArrowRight, ChevronUp, Minus, Star, StarHalf, X } from "lucide-react";
import { useState, type CSSProperties, type MouseEvent } from "react";
import type { GoogleDetails } from "../../../types";

export type GoogleReviewsStickyPosition = "bottom-left" | "bottom-right";

export type GoogleReviewsStickyProps = {
    googleDetails: GoogleDetails;
    title?: string;
    rating?: number | string;
    reviewCountText?: string;
    linkLabel?: string;
    minimiseLabel?: string;
    reviewsLink?: string;
    position?: GoogleReviewsStickyPosition;
    accentColor?: string;
    readReviewsColor?: string;
    backgroundColor?: string;
    opacity?: number;
    bottomOffset?: number | string;
    className?: string;
    defaultOpen?: boolean;
    initiallyOpen?: boolean;
    showCloseButton?: boolean;
    closeLabel?: string;
    compact?: boolean;
};

type CssVarStyle = CSSProperties & Record<`--${string}`, string | number | undefined>;

const GoogleGMark = ({ size = 44 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        aria-hidden="true"
        focusable="false"
    >
        <path fill="#4285F4" d="M47.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h13.2c-.6 3-2.3 5.6-4.9 7.3v6h7.9c4.6-4.3 7.3-10.6 7.3-17.3z" />
        <path fill="#34A853" d="M24 48c6.6 0 12.1-2.2 16.2-5.9l-7.9-6c-2.2 1.5-5 2.3-8.3 2.3-6.4 0-11.8-4.3-13.7-10.1H2.2v6.2C6.2 42.5 14.5 48 24 48z" />
        <path fill="#FBBC05" d="M10.3 28.3c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7v-6.2H2.2C.8 15.9 0 19.6 0 23.6s.8 7.7 2.2 11l8.1-6.3z" />
        <path fill="#EA4335" d="M24 9.5c3.6 0 6.8 1.2 9.3 3.7l7-7C36.1 2.3 30.6 0 24 0 14.5 0 6.2 5.5 2.2 12.7l8.1 6.2C12.2 13.8 17.6 9.5 24 9.5z" />
    </svg>
);

const googleReviewsStickyCss = `
.google-reviews-sticky-root {
    box-sizing: border-box;
}

.google-reviews-sticky-root * {
    box-sizing: border-box;
}

.google-reviews-sticky-summary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: calc(100vw - 1.5rem);
    padding: 8px 10px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 999px;
    cursor: pointer;
    user-select: none;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
    transition: transform 160ms ease, filter 160ms ease;
}

.google-reviews-sticky-summary:hover {
    transform: translateY(-1px);
    filter: brightness(0.99);
}

.google-reviews-sticky-summary:focus-visible {
    outline: 2px solid var(--google-reviews-sticky-accent);
    outline-offset: 3px;
}

.google-reviews-sticky-summary::-webkit-details-marker {
    display: none;
}

.google-reviews-sticky-summary::marker {
    content: "";
}

.google-reviews-sticky-summary-closed {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.google-reviews-sticky-summary-open {
    display: none;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: #171717;
    font-size: 11px;
    line-height: 1;
    font-weight: 800;
}

.google-reviews-sticky-summary-copy {
    display: grid;
    gap: 2px;
    min-width: 0;
}

.google-reviews-sticky-summary-title {
    display: block;
    color: #171717;
    font-size: 12px;
    line-height: 1;
    font-weight: 800;
}

.google-reviews-sticky-summary-meta {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #171717;
    font-size: 12px;
    line-height: 1;
    font-weight: 700;
}

.google-reviews-sticky-summary-stars {
    display: inline-flex;
    align-items: center;
    gap: 1px;
}

.google-reviews-sticky-panel {
    display: none;
}

.google-reviews-sticky-mobile-dock {
    display: none;
}

.google-reviews-sticky-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: -8px;
    right: -8px;
    z-index: 3;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 999px;
    color: #4b5563;
    background: #ffffff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.15);
    cursor: pointer;
    transition: transform 160ms ease, filter 160ms ease;
}

.google-reviews-sticky-close:hover {
    transform: translateY(-1px);
    filter: brightness(0.98);
}

.google-reviews-sticky-close:focus-visible {
    outline: 2px solid var(--google-reviews-sticky-accent);
    outline-offset: 3px;
}

.google-reviews-sticky-root[open] .google-reviews-sticky-summary {
    position: absolute;
    top: 10px;
    right: 46px;
    z-index: 1;
    width: auto;
    min-width: 28px;
    height: 28px;
    justify-content: center;
    padding: 0 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.google-reviews-sticky-root[open] .google-reviews-sticky-summary:hover {
    transform: none;
    filter: brightness(0.98);
}

.google-reviews-sticky-root[open] .google-reviews-sticky-summary-closed {
    display: none;
}

.google-reviews-sticky-root[open] .google-reviews-sticky-summary-open {
    display: inline-flex;
}

.google-reviews-sticky-root[open] .google-reviews-sticky-panel {
    display: block;
}

.google-reviews-sticky-root[open] .google-reviews-sticky-close {
    top: 10px;
    right: 10px;
}

.google-reviews-sticky-compact {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: calc(100vw - 1.5rem);
    padding: 5px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 999px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
}

.google-reviews-sticky-compact-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 999px;
    color: inherit;
    text-decoration: none;
    transition: transform 160ms ease, filter 160ms ease;
}

.google-reviews-sticky-compact-link:hover {
    transform: translateY(-1px);
    filter: brightness(0.99);
}

.google-reviews-sticky-compact-link:focus-visible {
    outline: 2px solid var(--google-reviews-sticky-accent);
    outline-offset: 3px;
}

.google-reviews-sticky-compact .google-reviews-sticky-close {
    position: static;
    width: 36px;
    height: 36px;
    box-shadow: none;
}

@media (max-width: 1024px) {
    .google-reviews-sticky-summary {
        display: none;
    }

    .google-reviews-sticky-panel {
        display: none;
    }

    .google-reviews-sticky-root[open] .google-reviews-sticky-panel {
        display: none;
    }

    .google-reviews-sticky-mobile-dock {
        display: inline-flex;
    }

    .google-reviews-sticky-root[open] .google-reviews-sticky-close {
        top: -8px;
        right: -8px;
    }
}

@media (max-width: 480px) {
    .google-reviews-sticky-mobile-dock {
        width: 44px;
        height: 44px;
    }
}
`;

const defaultBottomOffset = "1.5rem";

const toCssSize = (value?: number | string) => {
    if (typeof value === "number") return `${value}px`;
    return value;
};

const GoogleReviewsSticky = ({
    googleDetails,
    title = "Google Reviews",
    rating = "5.0",
    reviewCountText = "Based on 708 reviews",
    linkLabel = "Read Reviews",
    minimiseLabel = "Minimise",
    reviewsLink,
    position = "bottom-left",
    accentColor = "#ed1c24",
    readReviewsColor = accentColor,
    backgroundColor = "#ffffff",
    opacity = 1,
    bottomOffset = defaultBottomOffset,
    className = "",
    defaultOpen = false,
    initiallyOpen = true,
    showCloseButton = true,
    closeLabel = "Close Google reviews",
    compact = false,
}: GoogleReviewsStickyProps) => {
    const [isDismissed, setIsDismissed] = useState(false);
    const reviewHref =
        reviewsLink ||
        googleDetails.googleLink ||
        googleDetails.googleMaps ||
        googleDetails.googleReviewLink;

    if (!initiallyOpen || isDismissed || !reviewHref) {
        return null;
    }

    const formattedRating =
        typeof rating === "number" ? rating.toFixed(1) : rating;
    const numericRating = Number.parseFloat(String(rating));
    const roundedRating = Number.isFinite(numericRating)
        ? Math.min(Math.max(Math.round(numericRating * 2) / 2, 0), 5)
        : 5;
    const resolvedOpacity = Math.min(Math.max(opacity, 0), 1);
    const renderStars = (starClassName: string) =>
        Array.from({ length: 5 }).map((_, index) => {
            const starValue = index + 1;
            const isFull = roundedRating >= starValue;
            const isHalf =
                !isFull && roundedRating >= starValue - 0.5;
            const StarIcon = isHalf ? StarHalf : Star;

            return (
                <StarIcon
                    key={index}
                    className={`
                        ${starClassName} text-[#ffb300]
                        ${isFull || isHalf ? "fill-[#ffb300]" : "fill-transparent"}
                    `}
                    aria-hidden="true"
                />
            );
        });
    const dismissReviews = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDismissed(true);
    };
    const renderCloseButton = () => {
        if (!showCloseButton) return null;

        return (
            <button
                type="button"
                aria-label={closeLabel}
                className="google-reviews-sticky-close"
                onClick={dismissReviews}
            >
                <X className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
            </button>
        );
    };
    const rootStyle: CssVarStyle = {
        "--google-reviews-sticky-accent": accentColor,
        left: position === "bottom-left" ? "0.75rem" : "auto",
        right: position === "bottom-right" ? "0.75rem" : "auto",
        bottom: `calc(${toCssSize(bottomOffset) || defaultBottomOffset} + env(safe-area-inset-bottom))`,
        margin: 0,
        opacity: resolvedOpacity,
    };

    if (compact) {
        return (
            <aside
                aria-label="Google reviews"
                data-component="google-reviews-sticky"
                className={`
                    google-reviews-sticky-root fixed z-[60] overflow-visible border-0 bg-transparent
                    p-0 text-neutral-950
                    ${className}
                `}
                style={rootStyle}
            >
                <style>{googleReviewsStickyCss}</style>

                <div
                    className="google-reviews-sticky-compact"
                    style={{ backgroundColor }}
                >
                    <a
                        href={reviewHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Read Google reviews"
                        title="Read Google reviews"
                        className="google-reviews-sticky-compact-link"
                    >
                        <GoogleGMark size={30} />
                    </a>

                    {renderCloseButton()}
                </div>
            </aside>
        );
    }

    return (
        <details
            open={defaultOpen}
            aria-label="Google reviews"
            className={`
                google-reviews-sticky-root fixed z-[60] overflow-visible border-0 bg-transparent
                p-0 text-neutral-950
                ${className}
            `}
            style={rootStyle}
        >
            <style>{googleReviewsStickyCss}</style>
            {renderCloseButton()}

            <summary
                className="google-reviews-sticky-summary"
                style={{ backgroundColor }}
                aria-label="Toggle Google reviews"
            >
                <span className="google-reviews-sticky-summary-closed">
                    <GoogleGMark size={28} />
                    <span className="google-reviews-sticky-summary-copy">
                        <span className="google-reviews-sticky-summary-title">
                            {title}
                        </span>
                        <span className="google-reviews-sticky-summary-meta">
                            <span>{formattedRating}</span>
                            <span
                                aria-label={`${formattedRating} out of 5 stars`}
                                className="google-reviews-sticky-summary-stars"
                            >
                                {renderStars("h-3 w-3")}
                            </span>
                        </span>
                    </span>
                    <ChevronUp
                        className="h-3.5 w-3.5 text-neutral-500"
                        strokeWidth={2.4}
                        aria-hidden="true"
                    />
                </span>

                <span className="google-reviews-sticky-summary-open">
                    {showCloseButton ? (
                        <>
                            <Minus className="h-3.5 w-3.5" aria-hidden="true" />
                            <span>{minimiseLabel}</span>
                        </>
                    ) : (
                        <ChevronUp className="h-4 w-4" aria-hidden="true" />
                    )}
                </span>
            </summary>

            <div
                className="
                    google-reviews-sticky-panel relative
                    w-[calc(100vw-1.5rem)] max-w-[20rem] sm:w-[20rem]
                    rounded-lg border border-black/5 p-4 pr-10
                "
                style={{
                    backgroundColor,
                    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.16)",
                }}
            >
                <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center">
                        <GoogleGMark />
                    </div>

                    <div className="min-w-0">
                        <p className="mb-1.5 text-sm font-semibold leading-tight">
                            {title}
                        </p>

                        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-lg leading-none text-neutral-950">
                                {formattedRating}
                            </span>
                            <span
                                aria-label={`${formattedRating} out of 5 stars`}
                                className="flex items-center gap-0.5"
                            >
                                {renderStars("h-4 w-4")}
                            </span>
                        </div>

                        <p className="mb-4 text-xs leading-tight text-neutral-700">
                            {reviewCountText}
                        </p>

                        <a
                            href={reviewHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Read Google reviews"
                            className="
                                inline-flex items-center gap-1.5 text-sm
                                font-semibold no-underline transition-colors
                                hover:opacity-80 focus-visible:outline
                                focus-visible:outline-2 focus-visible:outline-offset-4
                            "
                            style={{ color: readReviewsColor, outlineColor: readReviewsColor }}
                        >
                            {linkLabel}
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                    </div>
                </div>
            </div>

            <a
                href={reviewHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Read Google reviews"
                title="Read Google reviews"
                className="
                    google-reviews-sticky-mobile-dock h-12 w-12 items-center justify-center
                    rounded-full border border-black/5 no-underline
                    transition-transform hover:-translate-y-0.5
                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3
                "
                style={{
                    backgroundColor,
                    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
                    outlineColor: accentColor,
                }}
            >
                <GoogleGMark size={30} />
            </a>
        </details>
    );
};

export default GoogleReviewsSticky;
